import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../common/Button';
import { useCurrentUser } from '../../context/UserContext';
import { classroomMeetingPath } from '../../api/classroom';
import { getNormalizedRole } from '../../utils/auth';
import { resolveClassroomPortalRole } from '../../utils/classroomPortalRole';

/**
 * Learner Join Session — navigates to in-app Zoom embed.
 * Server sets canJoin only when meetingStatus is STARTED (staff started first).
 * Embed-auth also rejects learners until STARTED — this UI is not the only gate.
 */
export default function SessionJoinAction({
  sessionId,
  role: roleProp,
  classroomEligible = false,
  canJoin = false,
  meetingStatus,
  status,
  endTime,
  studentId,
  className = '',
  compact = false,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const resolved = resolveClassroomPortalRole(roleProp, user, getNormalizedRole());
  const portalRole = resolved === 'PARENT' ? 'PARENT' : 'STUDENT';
  const ms = String(meetingStatus || '').toUpperCase();
  const meetingEnded = ms === 'ENDED';
  const meetingLive = ms === 'STARTED';
  // Defense in depth: never show Join unless server canJoin AND status is STARTED.
  const joinAllowed = Boolean(canJoin && meetingLive);

  const pastOrCancelled = useMemo(() => {
    if (joinAllowed) return false;
    const st = String(status || '').toUpperCase();
    if (st === 'PAST' || st === 'CANCELLED' || st === 'COMPLETED') return true;
    if (meetingEnded) return true;
    if (endTime) {
      const end = new Date(endTime);
      if (!Number.isNaN(end.getTime()) && end.getTime() < Date.now() - 2 * 60 * 60 * 1000) {
        return true;
      }
    }
    return false;
  }, [status, meetingEnded, joinAllowed, endTime]);

  const showJoin = Boolean(sessionId && joinAllowed && !pastOrCancelled);
  const showEnded = Boolean(sessionId && classroomEligible && meetingEnded && !joinAllowed);
  const showWaiting = Boolean(
    sessionId
    && classroomEligible
    && !joinAllowed
    && !meetingEnded
    && !pastOrCancelled
    && ms !== 'FAILED',
  );

  if (!showJoin && !showEnded && !showWaiting) {
    return null;
  }

  if (showEnded) {
    return (
      <span className={`text-xs font-medium text-slate-500 ${className}`} role="status">
        {t('classroom.live.meetingStatusEnded')}
      </span>
    );
  }

  if (showWaiting) {
    return (
      <span
        className={`text-xs font-medium text-amber-800 max-w-[14rem] text-center leading-snug ${className}`}
        role="status"
        data-classroom-waiting-start
        title={t('classroom.live.notStartedHint', {
          defaultValue: 'Your coach has not started this online class yet.',
        })}
      >
        {t('classroom.live.notStartedYet', { defaultValue: 'Not started yet' })}
      </span>
    );
  }

  const goJoin = () => {
    navigate(classroomMeetingPath(portalRole, sessionId, {
      studentId: portalRole === 'PARENT' ? studentId : undefined,
    }));
  };

  const joinLabel = t('classroom.live.joinSession', { defaultValue: 'Join Session' });

  return (
    <div className={`inline-flex flex-col items-center gap-1 ${className}`}>
      {compact ? (
        <button type="button" className="btn-action-activate" onClick={goJoin} data-classroom-join>
          {joinLabel}
        </button>
      ) : (
        <Button type="button" variant="page-primary" onClick={goJoin} data-classroom-join>
          {joinLabel}
        </Button>
      )}
    </div>
  );
}
